import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import kycService from "@/services/api/kyc.service";
import { useNavigation } from "@/contexts/notification/NavigationContext";

const KycNotification = () => {
  const navigate = useNavigate();
  const [kycStatus, setKycStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isVisible, setIsVisible] = useState(true);

  // Use the NavigationContext to track where the user came from
  const { trackPageVisit, lastVisitedPages } = useNavigation();

  const checkKycStatus = async () => {
    try {
      setLoading(true);
      const response = await kycService.checkKycStatus();

      if (response.status === "success") {
        setKycStatus(response.data);
      }
    } catch (err) {
      console.error("Error checking KYC status:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkKycStatus();
  }, []);

  const handleCompleteKyc = () => {
    // Store the current path before navigating to KYC
    const currentPath = window.location.pathname;

    // Ensure current path is tracked before navigating away
    if (lastVisitedPages.length === 0 || lastVisitedPages[0] !== currentPath) {
      trackPageVisit(currentPath);
    }

    navigate("/profile/kyc/status");
  };

  const handleClose = () => {
    setIsVisible(false);
  };

  const validStatuses = ["not_started", "pending", "draft", "rejected"];

  if (
    loading ||
    !kycStatus ||
    kycStatus.isKycComplete ||
    !validStatuses.includes(kycStatus.kycStatus) ||
    !isVisible
  ) {
    return null;
  }

  const { missingItems, completionPercentage } = kycStatus;

  let message = "";
  let actionLabel = "Check KYC Status";

  if (kycStatus.kycStatus === "not_started") {
    message = "KYC not started - some features limited";
    actionLabel = "Start KYC Process";
  } else if (kycStatus.kycStatus === "rejected") {
    message = "KYC Rejected - Please update your details";
    actionLabel = "Update KYC";
  } else if (completionPercentage === 100) {
    message = "KYC approval pending - some features limited";
  } else {
    message = `KYC ${completionPercentage || 0}% complete`;
    if (missingItems?.length > 0) {
      message += ` (Missing: ${missingItems.join(", ")})`;
      actionLabel = "Complete Now";
    }
  }

  return (
    <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50">
      <div className="bg-white rounded-lg shadow-lg border border-gray-200 max-w-md w-full mx-4 overflow-hidden">
        <div className="p-3 flex items-start justify-between bg-yellow-50">
          <div className="flex items-start">
            <div className="flex-shrink-0 pt-0.5">
              <svg
                className="h-5 w-5 text-yellow-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <div className="ml-2">
              <p className="text-sm font-medium text-gray-900">KYC Required</p>
              <p className="text-xs text-gray-600 mt-0.5 min-h-[1.25rem]">
                {message}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="flex-shrink-0 ml-2 text-gray-400 hover:text-gray-600 focus:outline-none"
            aria-label="Close notification"
          >
            <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>

        <div className="px-3 pb-2">
          <div className="w-full bg-gray-200 rounded-full h-1.5">
            <div
              className="bg-yellow-500 h-1.5 rounded-full"
              style={{ width: `${completionPercentage}%` }}
            ></div>
          </div>
        </div>

        <div className="px-3 pb-3">
          <button
            onClick={handleCompleteKyc}
            className="w-full py-1.5 px-3 rounded-md text-xs font-medium bg-yellow-500 text-white hover:bg-yellow-600 transition-colors"
          >
            {actionLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default KycNotification;
